/**
 * ShareModalView - Reusable modal for sharing entities with organization teammates
 *
 * Renders a centered modal with search, "Select All", a scrollable user list
 * with checkboxes and avatar initials, an optional children slot (for e.g.
 * existing share recipients), and Cancel/Share footer buttons.
 *
 * Both ReportShareSheet and NoteShareSheet delegate their modal rendering
 * here to eliminate duplication of the identical modal structure.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import {
  ArrowLeftIcon,
  CheckIcon,
  SearchIcon,
  UsersIcon,
  XIcon,
} from "lucide-react-native";
import { memo, type ReactNode } from "react";
import { Platform } from "react-native";

import UserSelectItem from "@/components/organisms/shared/UserSelectItem";
import type { AvailableUserDisplayData } from "@/components/organisms/shared/useShareModal";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
} from "@/components/atoms/Checkbox";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

/** Props for ShareModalView */
export interface ShareModalViewProps {
  /** Whether the modal is visible */
  readonly isOpen: boolean;
  /** Callback when the modal is dismissed */
  readonly onClose: () => void;
  /** Available organization users to share with */
  readonly availableUsers: readonly AvailableUserDisplayData[];
  /** Currently selected user IDs */
  readonly selectedUserIds: readonly string[];
  /** Whether all visible users are selected */
  readonly allSelected: boolean;
  /** Whether user data is loading */
  readonly loading: boolean;
  /** Whether the share mutation is in progress */
  readonly isSharing: boolean;
  /** Callback to toggle a user's selection */
  readonly onToggleUser: (userId: string) => void;
  /** Callback to toggle select all */
  readonly onToggleAll: () => void;
  /** Callback when share is pressed */
  readonly onShare: () => void;
  /** Callback when cancel is pressed */
  readonly onCancel: () => void;
  /** Current search text */
  readonly searchText: string;
  /** Callback when search text changes */
  readonly onSearchChange: (text: string) => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** User IDs that are pre-selected (already shared) and cannot be toggled off */
  readonly preSelectedUserIds?: readonly string[];
  /** Descriptive subtitle shown under the heading (e.g. "Select teammates who should be able to view this report.") */
  readonly subtitle: string;
  /** Top safe area inset for full-screen layout on native devices */
  readonly topSafeArea?: number;
  /** Optional content rendered between the user list and footer (e.g. existing share recipients) */
  readonly children?: ReactNode;
}

/**
 * Returns the background class for modal content.
 * Theme-aware via the surface-base token (was an isDark zinc-900/white
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getContentBgClass = (_isDark: boolean): string => "bg-surface-base";

/**
 * Returns the border class.
 * Theme-aware via the outline-subtle token (was an isDark
 * zinc-700/zinc-200 ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getBorderClass = (_isDark: boolean): string => "border-outline-subtle";

/**
 * Returns the muted text class.
 * Theme-aware via the content-muted token (was an isDark zinc-400/zinc-500
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getMutedTextClass = (_isDark: boolean): string => "text-content-muted";

/**
 * Returns the foreground text class.
 * Theme-aware via the content-primary token (was an isDark white/zinc-900
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getFgTextClass = (_isDark: boolean): string => "text-content-primary";

/**
 * Returns themed placeholder color for search input
 * @param isDark - Whether dark mode is active
 */
const getPlaceholderColor = (isDark: boolean): string =>
  isDark ? "#A1A1AA" : "#71717A";

/** Whether the current platform is native (not web) */
const IS_NATIVE = Platform.OS !== "web";

/** Style for the scrollable user list container on web */

/**
 * Returns the full-screen background class for native modal content
 * @param isDark - Whether dark mode is active
 */
const getFullScreenBgClass = (isDark: boolean): string =>
  isDark ? "bg-black" : "bg-white";

/**
 * Renders the share modal with user multi-select, search, and optional children
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is visible
 * @param root0.onClose - Callback when the modal is dismissed
 * @param root0.availableUsers - Available organization users to share with
 * @param root0.selectedUserIds - Currently selected user IDs
 * @param root0.allSelected - Whether all visible users are selected
 * @param root0.loading - Whether user data is loading
 * @param root0.isSharing - Whether the share mutation is in progress
 * @param root0.onToggleUser - Callback to toggle a user's selection
 * @param root0.onToggleAll - Callback to toggle select all
 * @param root0.onShare - Callback when share is pressed
 * @param root0.onCancel - Callback when cancel is pressed
 * @param root0.searchText - Current search text
 * @param root0.onSearchChange - Callback when search text changes
 * @param root0.isDark - Whether dark mode is active
 * @param root0.preSelectedUserIds - User IDs that are pre-selected (already shared) and cannot be toggled off
 * @param root0.subtitle - Descriptive subtitle shown under the heading
 * @param root0.topSafeArea - Top safe area inset for full-screen layout on native devices
 * @param root0.children - Optional content rendered between the user list and footer
 */
const ShareModalView = ({
  isOpen,
  onClose,
  availableUsers,
  selectedUserIds,
  allSelected,
  loading,
  isSharing,
  onToggleUser,
  onToggleAll,
  onShare,
  onCancel,
  searchText,
  onSearchChange,
  isDark,
  preSelectedUserIds,
  subtitle,
  topSafeArea,
  children,
}: ShareModalViewProps) => (
  <Modal
    useRNModal={IS_NATIVE}
    isOpen={isOpen}
    onClose={onClose}
    size={IS_NATIVE ? "full" : "md"}
  >
    <ModalBackdrop />
    <ModalContent
      className={
        IS_NATIVE
          ? `size-full rounded-none border-0 border-none ${getFullScreenBgClass(isDark)} p-0 shadow-none`
          : `${getContentBgClass(isDark)} max-w-lg`
      }
      // eslint-disable-next-line no-restricted-syntax -- safe-area top inset (topSafeArea prop) is a measured runtime value with no closed sizing token; ModalContent is a bare re-export atom with no UNSAFE_style prop
      style={IS_NATIVE ? { paddingTop: topSafeArea ?? 0 } : undefined}
    >
      {/* Header */}
      <ModalHeader
        className={`border-b px-6 pb-4 pt-6 ${getBorderClass(isDark)}`}
      >
        {IS_NATIVE ? (
          <Stack className="w-full gap-4">
            <Pressable onPress={onClose}>
              <Stack direction="horizontal" className="items-center gap-2">
                <Icon
                  as={ArrowLeftIcon}
                  size="lg"
                  className={getFgTextClass(isDark)}
                />
                <Text
                  variant="body"
                  className={`font-medium ${getFgTextClass(isDark)}`}
                >
                  Back
                </Text>
              </Stack>
            </Pressable>
            <Stack className="gap-1">
              <Heading variant="title-lg" className={getFgTextClass(isDark)}>
                Share with Others
              </Heading>
              <Text variant="body" className={getMutedTextClass(isDark)}>
                {subtitle}
              </Text>
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction="horizontal"
            className="w-full items-start justify-between"
          >
            <Stack direction="horizontal" className="items-center gap-3">
              <Icon
                as={UsersIcon}
                size="xl"
                className={getFgTextClass(isDark)}
              />
              <Stack>
                <Heading variant="title-lg" className={getFgTextClass(isDark)}>
                  Share with Others
                </Heading>
                <Text variant="body" className={getMutedTextClass(isDark)}>
                  {subtitle}
                </Text>
              </Stack>
            </Stack>
            <Stack className="pt-1">
              <Pressable onPress={onClose}>
                <Icon
                  as={XIcon}
                  size="lg"
                  className={getMutedTextClass(isDark)}
                />
              </Pressable>
            </Stack>
          </Stack>
        )}
      </ModalHeader>

      {/* Body */}
      <ModalBody className="px-6 py-4">
        {loading ? (
          <Box className="flex items-center justify-center py-8">
            <Spinner size="large" />
          </Box>
        ) : (
          <Stack className="gap-4">
            {/* Search */}
            <Stack
              direction="horizontal"
              className={`items-center gap-2 rounded-sm border px-3 py-2 ${getBorderClass(isDark)}`}
            >
              <Icon
                as={SearchIcon}
                size="sm"
                className={getMutedTextClass(isDark)}
              />
              <TextInput
                value={searchText}
                onChangeText={onSearchChange}
                placeholder="Search teammates..."
                placeholderTextColor={getPlaceholderColor(isDark)}
                // `min-w-0 web:text-ellipsis` is this field's narrow-width
                // failure mode (SE-7731). See the note at the same class on
                // HeaderSearchButtonView: `flex-1` alone cannot shrink an
                // `<input>` below its UA min-content width, so the field paints
                // outside this row's border on a narrow modal instead of
                // truncating.
                className={`web:text-ellipsis web:py-2 min-w-0 flex-1 text-body ${getFgTextClass(isDark)}`}
              />
            </Stack>

            {/* Select All */}
            <Pressable onPress={onToggleAll}>
              <Stack
                direction="horizontal"
                className={`items-center gap-3 border-b pb-3 ${getBorderClass(isDark)}`}
              >
                <Box className="pointer-events-none">
                  <Checkbox value="all" isChecked={allSelected} size="md">
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                  </Checkbox>
                </Box>
                <Text
                  variant="body"
                  className={`font-medium ${getFgTextClass(isDark)}`}
                >
                  Select All
                </Text>
              </Stack>
            </Pressable>

            {/* User list - full height on native, constrained on web */}
            <ScrollView bounces={false} className="web:max-h-80">
              {availableUsers.length === 0 ? (
                <Box className="items-center justify-center py-8">
                  <Text
                    variant="unstyled"
                    className={getMutedTextClass(isDark)}
                  >
                    No teammates found
                  </Text>
                </Box>
              ) : (
                availableUsers.map(user => (
                  <UserSelectItem
                    key={user.userId}
                    userId={user.userId}
                    displayName={user.displayName}
                    initials={user.initials}
                    title={user.title}
                    isSelected={selectedUserIds.includes(user.userId)}
                    isDark={isDark}
                    onToggleUser={onToggleUser}
                  />
                ))
              )}
            </ScrollView>

            {/* Feature-specific content (e.g. existing share recipients) */}
            {children}
          </Stack>
        )}
      </ModalBody>

      {/* Footer */}
      {!loading && (
        <ModalFooter className={`border-t px-6 py-4 ${getBorderClass(isDark)}`}>
          <Stack
            direction="horizontal"
            className="w-full items-center justify-end gap-3"
          >
            <Button
              variant="outline"
              size="md"
              onPress={onCancel}
              className={`rounded-sm border ${getBorderClass(isDark)}`}
              label="Cancel"
              labelTone="primary"
            />
            <Button
              size="md"
              onPress={onShare}
              isDisabled={
                isSharing ||
                selectedUserIds.filter(
                  id => !(preSelectedUserIds ?? []).includes(id)
                ).length === 0
              }
              className="rounded-sm bg-status-info data-[hover=true]:bg-needs-semantic-info-600"
              isLoading={isSharing}
              icon={UsersIcon}
              label="Share"
              labelTone="on-accent"
              iconTone="on-accent"
            />
          </Stack>
        </ModalFooter>
      )}
    </ModalContent>
  </Modal>
);

ShareModalView.displayName = "ShareModalView";

export default memo(ShareModalView);
